import type { CartLine, CartProductSnapshot, Product, Variant } from "./types";
export const maxQuantity = (variant: Variant) =>
  !Number.isFinite(variant.price) || variant.price <= 0
    ? 0
    : variant.made_to_order
      ? 10
      : Math.min(10, variant.stock);
export const previewVariantId = (productId: string) => `${productId}::preview`;
export const cartLineLimit = (line: CartLine, variant: Variant) => line.preview ? 10 : maxQuantity(variant);

function readCartProductSnapshot(value: unknown): CartProductSnapshot | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const item = value as Record<string, unknown>;
  if (
    typeof item.id !== "string" || item.id.length > 100 ||
    typeof item.slug !== "string" || item.slug.length > 180 ||
    typeof item.name !== "string" || !Array.isArray(item.variants)
  ) return undefined;
  const variants = item.variants.flatMap((value): Variant[] => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return [];
    const variant = value as Record<string, unknown>;
    if (typeof variant.id !== "string" || typeof variant.color !== "string") return [];
    const price = Number(variant.price);
    const stock = Number(variant.stock);
    if (!Number.isFinite(price) || !Number.isFinite(stock)) return [];
    return [{
      id: variant.id.slice(0, 100), color: variant.color.slice(0, 80),
      hex: typeof variant.hex === "string" ? variant.hex.slice(0, 40) : "",
      price: Math.max(0, price), stock: Math.max(0, stock), made_to_order: variant.made_to_order === true,
    }];
  }).slice(0, 20);
  const images = Array.isArray(item.images) ? item.images.flatMap((image) => {
    if (typeof image !== "string" || image.length > 2048) return [];
    if (image.startsWith("/")) return [image];
    try { return new URL(image).protocol === "https:" ? [image] : []; } catch { return []; }
  }).slice(0, 2) : [];
  return {
    id: item.id,
    slug: item.slug,
    name: item.name.slice(0, 180),
    subtitle: typeof item.subtitle === "string" ? item.subtitle.slice(0, 240) : "",
    images,
    variants,
    basePrice: Number.isFinite(Number(item.basePrice)) ? Math.max(0, Number(item.basePrice)) : undefined,
    oldPrice: Number.isFinite(Number(item.oldPrice)) ? Math.max(0, Number(item.oldPrice)) : null,
    demo: item.demo === true,
    purchasable: item.purchasable === true,
  };
}

export function parseCart(value: unknown): CartLine[] {
  if (!Array.isArray(value)) return [];
  const merged = new Map<string, { quantity: number; preview: boolean; productSnapshot?: CartProductSnapshot }>();
  for (const line of value.slice(0, 100)) {
    if (
      !line ||
      typeof line.variantId !== "string" ||
      line.variantId.length > 100 ||
      !Number.isInteger(line.quantity) ||
      line.quantity < 1
    )
      continue;
    const old = merged.get(line.variantId);
    merged.set(line.variantId, {
      quantity: Math.min(10, (old?.quantity || 0) + line.quantity),
      preview: Boolean(old?.preview || line.preview === true),
      productSnapshot: old?.productSnapshot ?? readCartProductSnapshot(line.productSnapshot),
    });
  }
  return [...merged].map(([variantId, line]) => ({
    variantId,
    quantity: line.quantity,
    ...(line.preview ? { preview: true as const } : {}),
    ...(line.productSnapshot ? { productSnapshot: line.productSnapshot } : {}),
  }));
}
export function resolveCart(lines: CartLine[], products: Product[]) {
  return lines.flatMap((line) => {
    const product = products.find((p) =>
      p.variants.some((v) => v.id === line.variantId),
    ) ?? line.productSnapshot ?? (line.preview ? products.find((p) => previewVariantId(p.id) === line.variantId) : undefined);
    const variant = product?.variants.find((v) => v.id === line.variantId) ?? (product && line.preview ? {
      id: line.variantId,
      color: product.variants[0]?.color || "انتخاب نشده",
      hex: "",
      price: Number.isFinite(product.basePrice) && (product.basePrice ?? 0) > 0 ? product.basePrice! : 0,
      stock: 10,
      made_to_order: false,
    } : undefined);
    return product && variant ? [{ ...line, product, variant }] : [];
  });
}
export function quoteCart(input: unknown, products: Product[]) {
  if (
    !Array.isArray(input) ||
    input.length > 100 ||
    input.some(
      (line) =>
        !line ||
        !Number.isInteger(line.quantity) ||
        line.quantity < 1 ||
        line.quantity > 10,
    )
  )
    throw new Error("تعداد محصولات معتبر نیست.");
  const lines = parseCart(input);
  if (lines.some((line) => line.preview)) {
    throw new Error("برخی کالاهای سبد قابل سفارش نیستند. آن‌ها را حذف کنید و دوباره تلاش کنید.");
  }
  if (!Array.isArray(input) || !lines.length || lines.length !== input.length)
    throw new Error("سبد خرید معتبر نیست.");
  // Never trust browser-saved snapshots for prices or order validation.
  const items = resolveCart(lines.map((line) => ({
    variantId: line.variantId,
    quantity: line.quantity,
    ...(line.preview ? { preview: true } : {}),
  })), products);
  if (
    items.length !== lines.length ||
    items.some((i) => i.product.demo || i.product.purchasable === false || i.quantity > maxQuantity(i.variant))
  )
    throw new Error(
      "موجودی یا مشخصات محصول تغییر کرده است. سبد خرید را بررسی کنید.",
    );
  return {
    items: items.map((i) => ({
      variantId: i.variant.id,
      name: i.product.name,
      color: i.variant.color,
      quantity: i.quantity,
      unitPrice: i.variant.price,
    })),
    subtotal: items.reduce((s, i) => s + i.variant.price * i.quantity, 0),
    shipping: null,
  };
}
